import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ARCHETYPES as DEFAULT_ARCHETYPES } from '../data/archetypes';
import { RESOURCES as DEFAULT_RESOURCES } from '../data/resources';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContentContext = createContext({
  archetypes: DEFAULT_ARCHETYPES,
  resources: DEFAULT_RESOURCES,
  loaded: false,
});

function deepMerge(base, override) {
  if (Array.isArray(base) && Array.isArray(override)) {
    // Merge arrays by index: overrides win element-wise; extra override items append.
    const out = base.map((b, i) => (
      override[i] === undefined ? b :
      (typeof b === 'object' && b !== null && typeof override[i] === 'object' && override[i] !== null)
        ? deepMerge(b, override[i])
        : override[i]
    ));
    if (override.length > base.length) out.push(...override.slice(base.length));
    return out;
  }
  if (base && typeof base === 'object' && override && typeof override === 'object' && !Array.isArray(base) && !Array.isArray(override)) {
    const out = { ...base };
    for (const k of Object.keys(override)) {
      out[k] = (k in base) ? deepMerge(base[k], override[k]) : override[k];
    }
    return out;
  }
  return override === undefined ? base : override;
}

export function ContentProvider({ children }) {
  const [overrides, setOverrides] = useState({ archetype: {}, resource: {} });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/content/overrides`, { timeout: 8000 })
      .then(({ data }) => {
        if (!cancelled) setOverrides(data || { archetype: {}, resource: {} });
      })
      .catch(() => { /* use defaults */ })
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  const value = useMemo(() => {
    const merged = {
      archetypes: { ...DEFAULT_ARCHETYPES },
      resources: { ...DEFAULT_RESOURCES },
      loaded,
    };
    for (const k of Object.keys(DEFAULT_ARCHETYPES)) {
      const o = overrides.archetype?.[k];
      if (o) merged.archetypes[k] = deepMerge(DEFAULT_ARCHETYPES[k], o);
    }
    for (const k of Object.keys(DEFAULT_RESOURCES)) {
      const o = overrides.resource?.[k];
      if (o) merged.resources[k] = deepMerge(DEFAULT_RESOURCES[k], o);
    }
    return merged;
  }, [overrides, loaded]);

  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
}

export function useContent() {
  return useContext(ContentContext);
}

export function useArchetypes() {
  return useContent().archetypes;
}

export function useResources() {
  return useContent().resources;
}
